import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { BarChart2, TrendingUp, ShoppingBag, AlertCircle, User, Wrench, CheckCircle2, Clock } from 'lucide-react';
import api from '../utils/api';
import { format, parseISO } from 'date-fns';

const MONTH_HE = ['ינו', 'פבר', 'מרס', 'אפר', 'מאי', 'יונ', 'יול', 'אוג', 'ספט', 'אוק', 'נוב', 'דצמ'];
const MONTH_FULL_HE = ['ינואר', 'פברואר', 'מרס', 'אפריל', 'מאי', 'יוני', 'יולי', 'אוגוסט', 'ספטמבר', 'אוקטובר', 'נובמבר', 'דצמבר'];

function monthLabel(yyyyMM) {
  const [y, m] = yyyyMM.split('-');
  return `${MONTH_HE[parseInt(m, 10) - 1]} ${y.slice(2)}`;
}

const currentYear = new Date().getFullYear();
const YEARS = Array.from({ length: 5 }, (_, i) => currentYear - i);

const selectCls = `px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-600
  bg-white dark:bg-gray-800 text-gray-900 dark:text-white
  focus:outline-none focus:ring-2 focus:ring-primary-500`;

const BAR_HEIGHT = 160;

export default function Sales() {
  const navigate = useNavigate();
  const [ordersData, setOrdersData] = useState(null);
  const [scData, setScData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState(() => {
    try { return localStorage.getItem('sales_selectedYear') ?? String(currentYear); } catch { return String(currentYear); }
  });
  const [selectedMonth, setSelectedMonth] = useState(() => {
    try { return localStorage.getItem('sales_selectedMonth') ?? ''; } catch { return ''; }
  });

  useEffect(() => { localStorage.setItem('sales_selectedYear', selectedYear); }, [selectedYear]);
  useEffect(() => { localStorage.setItem('sales_selectedMonth', selectedMonth); }, [selectedMonth]);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (selectedYear) params.set('year', selectedYear);
    if (selectedMonth) params.set('month', selectedMonth);
    const qs = params.toString() ? `?${params.toString()}` : '';
    Promise.all([
      api.get(`/orders/stats${qs}`),
      api.get(`/service-calls/stats${selectedYear ? `?year=${selectedYear}` : ''}`),
    ])
      .then(([ordersRes, scRes]) => {
        setOrdersData(ordersRes.data);
        setScData(scRes.data);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [selectedYear, selectedMonth]);

  const monthly = ordersData?.monthly || [];
  const totals = ordersData?.totals || {};
  const unpaid = ordersData?.unpaid || [];
  const scMonthly = scData?.monthly || [];
  const scTotals = scData?.totals || {};

  // Merge monthly data — union of all months, sorted
  const allMonths = [...new Set([...monthly.map((m) => m.month), ...scMonthly.map((m) => m.month)])].sort();
  const mergedMonthly = allMonths.map((month) => {
    const o = monthly.find((m) => m.month === month) || {};
    const sc = scMonthly.find((s) => s.month === month) || {};
    return {
      month,
      revenue: parseFloat(o.revenue) || 0,
      order_count: parseInt(o.order_count) || 0,
      call_count: parseInt(sc.call_count) || 0,
    };
  });

  const maxRevenue = Math.max(...mergedMonthly.map((m) => m.revenue), 1);
  const maxOrders = Math.max(...mergedMonthly.map((m) => m.order_count), 1);
  const maxCalls = Math.max(...mergedMonthly.map((m) => m.call_count), 1);

  const chartTitle = selectedYear ? `12 חודשים — ${selectedYear}` : '12 חודשים';

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <BarChart2 className="text-primary-600 dark:text-primary-400" size={22} />
          <h1 className="text-xl font-bold text-gray-900 dark:text-white">מכירות</h1>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={selectedYear}
            onChange={(e) => { setSelectedYear(e.target.value); setSelectedMonth(''); }}
            className={selectCls}
          >
            <option value="">12 חודשים אחרונים</option>
            {YEARS.map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
          <select
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
            className={selectCls}
          >
            <option value="">כל החודשים</option>
            {MONTH_FULL_HE.map((name, i) => (
              <option key={i + 1} value={i + 1}>{name}</option>
            ))}
          </select>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-20">
          <div className="animate-spin h-8 w-8 border-2 border-primary-500 border-t-transparent rounded-full" />
        </div>
      ) : (
        <>
          {/* Orders summary cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard
              icon={<ShoppingBag size={18} />}
              label="סה״כ הזמנות"
              value={parseInt(totals.total_orders || 0).toLocaleString()}
              color="blue"
            />
            <StatCard
              icon={<TrendingUp size={18} />}
              label="סה״כ הכנסות"
              value={`₪${parseFloat(totals.total_revenue || 0).toLocaleString('he-IL', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`}
              color="emerald"
            />
            <StatCard
              icon={<AlertCircle size={18} />}
              label="הזמנות לא שולמו"
              value={parseInt(totals.unpaid_count || 0).toLocaleString()}
              color="red"
            />
            <StatCard
              icon={<AlertCircle size={18} />}
              label="חוב פתוח"
              value={`₪${parseFloat(totals.unpaid_amount || 0).toLocaleString('he-IL', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`}
              color="red"
            />
          </div>

          {/* Service call summary cards */}
          <div className="grid grid-cols-3 gap-4">
            <StatCard
              icon={<Wrench size={18} />}
              label="סה״כ קריאות שירות"
              value={parseInt(scTotals.total_calls || 0).toLocaleString()}
              color="violet"
            />
            <StatCard
              icon={<Clock size={18} />}
              label="קריאות פתוחות"
              value={parseInt(scTotals.open_calls || 0).toLocaleString()}
              color="amber"
            />
            <StatCard
              icon={<CheckCircle2 size={18} />}
              label="קריאות סגורות"
              value={parseInt(scTotals.closed_calls || 0).toLocaleString()}
              color="emerald"
            />
          </div>

          {/* Grouped bar chart */}
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-semibold text-gray-900 dark:text-white text-sm">{chartTitle}</h2>
              <div className="flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400">
                <span className="flex items-center gap-1">
                  <span className="w-2.5 h-2.5 rounded-sm bg-emerald-500 inline-block" />
                  הכנסות
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-2.5 h-2.5 rounded-sm bg-primary-500 inline-block" />
                  הזמנות
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-2.5 h-2.5 rounded-sm bg-violet-500 inline-block" />
                  קריאות
                </span>
              </div>
            </div>

            {mergedMonthly.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-8">אין נתונים</p>
            ) : (
              <div className="flex items-end gap-1" style={{ height: `${BAR_HEIGHT + 20}px` }} dir="ltr">
                {mergedMonthly.map((m) => {
                  const revH = maxRevenue > 0 ? (m.revenue / maxRevenue) * BAR_HEIGHT : 0;
                  const ordH = maxOrders > 0 ? (m.order_count / maxOrders) * BAR_HEIGHT : 0;
                  const callH = maxCalls > 0 ? (m.call_count / maxCalls) * BAR_HEIGHT : 0;
                  const isCurrent = m.month === `${currentYear}-${String(new Date().getMonth() + 1).padStart(2, '0')}`;
                  return (
                    <div key={m.month} className="flex-1 flex flex-col items-center gap-1">
                      <div className="flex items-end gap-px w-full" style={{ height: `${BAR_HEIGHT}px` }}>
                        {/* Revenue */}
                        <div className="relative flex-1 flex flex-col justify-end group/bar">
                          {m.revenue > 0 && (
                            <div className="absolute bottom-full mb-1 left-1/2 -translate-x-1/2 hidden group-hover/bar:block bg-gray-900 text-white text-xs rounded px-1.5 py-0.5 whitespace-nowrap z-10">
                              ₪{m.revenue.toLocaleString('he-IL', { maximumFractionDigits: 0 })}
                            </div>
                          )}
                          <div
                            className={`w-full rounded-t-sm transition-all ${isCurrent ? 'bg-emerald-400' : 'bg-emerald-500 hover:bg-emerald-600'}`}
                            style={{ height: `${Math.max(revH, m.revenue > 0 ? 3 : 0)}px` }}
                          />
                        </div>
                        {/* Orders */}
                        <div className="relative flex-1 flex flex-col justify-end group/bar">
                          {m.order_count > 0 && (
                            <div className="absolute bottom-full mb-1 left-1/2 -translate-x-1/2 hidden group-hover/bar:block bg-gray-900 text-white text-xs rounded px-1.5 py-0.5 whitespace-nowrap z-10">
                              {m.order_count} הזמנות
                            </div>
                          )}
                          <div
                            className={`w-full rounded-t-sm transition-all ${isCurrent ? 'bg-primary-400' : 'bg-primary-500 hover:bg-primary-600'}`}
                            style={{ height: `${Math.max(ordH, m.order_count > 0 ? 3 : 0)}px` }}
                          />
                        </div>
                        {/* Service calls */}
                        <div className="relative flex-1 flex flex-col justify-end group/bar">
                          {m.call_count > 0 && (
                            <div className="absolute bottom-full mb-1 left-1/2 -translate-x-1/2 hidden group-hover/bar:block bg-gray-900 text-white text-xs rounded px-1.5 py-0.5 whitespace-nowrap z-10">
                              {m.call_count} קריאות
                            </div>
                          )}
                          <div
                            className={`w-full rounded-t-sm transition-all ${isCurrent ? 'bg-violet-400' : 'bg-violet-500 hover:bg-violet-600'}`}
                            style={{ height: `${Math.max(callH, m.call_count > 0 ? 3 : 0)}px` }}
                          />
                        </div>
                      </div>
                      <span className={`text-xs ${isCurrent ? 'text-amber-500 font-semibold' : 'text-gray-400 dark:text-gray-500'}`}>
                        {monthLabel(m.month)}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Unpaid orders */}
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2 text-sm">
                <AlertCircle size={16} className="text-red-500" />
                הזמנות שלא שולמו ({unpaid.length})
              </h2>
            </div>

            {unpaid.length === 0 ? (
              <p className="px-5 py-8 text-center text-sm text-gray-400">כל ההזמנות שולמו</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-700">
                      <th className="px-4 py-3 text-right font-medium text-gray-500 dark:text-gray-400">#</th>
                      <th className="px-4 py-3 text-right font-medium text-gray-500 dark:text-gray-400">לקוח</th>
                      <th className="px-4 py-3 text-right font-medium text-gray-500 dark:text-gray-400">עיר</th>
                      <th className="px-4 py-3 text-right font-medium text-gray-500 dark:text-gray-400">סכום</th>
                      <th className="px-4 py-3 text-right font-medium text-gray-500 dark:text-gray-400">תאריך</th>
                    </tr>
                  </thead>
                  <tbody>
                    {unpaid.map((o) => (
                      <tr
                        key={o.id}
                        onClick={() => navigate(`/customers/${o.customer_id}`)}
                        className="border-b border-gray-100 dark:border-gray-700/50 hover:bg-red-50 dark:hover:bg-red-900/20 cursor-pointer"
                      >
                        <td className="px-4 py-3 font-mono text-gray-500 dark:text-gray-400">#{o.order_number}</td>
                        <td className="px-4 py-3">
                          <span className="flex items-center gap-1.5 text-primary-600 dark:text-primary-400 font-medium">
                            <User size={13} />
                            {o.customer_name}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-gray-500 dark:text-gray-400">{o.customer_city || '—'}</td>
                        <td className="px-4 py-3 font-semibold text-red-600 dark:text-red-400">
                          {o.total_amount ? `₪${Number(o.total_amount).toLocaleString()}` : '—'}
                        </td>
                        <td className="px-4 py-3 text-gray-400 text-xs">
                          {format(parseISO(o.created_at), 'dd/MM/yyyy')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function StatCard({ icon, label, value, color }) {
  const colors = {
    blue:    'bg-blue-50    dark:bg-blue-900/20    text-blue-600    dark:text-blue-400',
    emerald: 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400',
    red:     'bg-red-50     dark:bg-red-900/20     text-red-600     dark:text-red-400',
    violet:  'bg-violet-50  dark:bg-violet-900/20  text-violet-600  dark:text-violet-400',
    amber:   'bg-amber-50   dark:bg-amber-900/20   text-amber-600   dark:text-amber-400',
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
      <div className={`inline-flex p-2 rounded-lg ${colors[color]} mb-3`}>
        {icon}
      </div>
      <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">{label}</p>
      <p className="text-xl font-bold text-gray-900 dark:text-white" dir="ltr">{value}</p>
    </div>
  );
}
